<!-- components/RichTextEditor.vue -->
<!--
  USAGE:
  <RichTextEditor v-model="content" label="Contenu de l'article" />
  <RichTextEditor v-model="summary" label="Résumé" :minHeight="150" />

  Pour afficher le contenu formaté dans n'importe quelle page :
  <div class="rte-content" v-html="content"></div>
  Ou en CSS pur :
  <div class="rte-content" [innerHTML injecté côté serveur]></div>

  Inclure le <style> de ce fichier (section .rte-content) globalement
  ou dans votre fichier CSS principal pour que le rendu soit identique
  partout (pages de lecture, aperçu, emails…).
-->
<template>
  <div
    class="rte-wrapper"
    :class="{ 'rte-focused': isFocused }"
    :style="{ '--rte-min-height': minHeight + 'px' }"
    :data-rte-id="uid"
  >
    <!-- Label optionnel -->
    <label v-if="label" class="rte-label">{{ label }}</label>

    <!-- ───── Toolbar ───── -->
    <div class="rte-toolbar" role="toolbar" :aria-label="label || 'Éditeur de texte'">

      <!-- Historique -->
      <div class="rte-group">
        <button type="button" class="rte-btn" :disabled="historyIndex <= 0"
          @click="undo" title="Annuler (Ctrl+Z)" aria-label="Annuler">
          <svg viewBox="0 0 24 24"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>
        </button>
        <button type="button" class="rte-btn" :disabled="historyIndex >= history.length - 1"
          @click="redo" title="Rétablir (Ctrl+Y)" aria-label="Rétablir">
          <svg viewBox="0 0 24 24"><path d="M18.4 10.6C16.55 9 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L2.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.96 0 3.73.72 5.12 1.88L11.5 16h9v-9l-3.6 3.6z"/></svg>
        </button>
      </div>

      <!-- Titres -->
      <div class="rte-group">
        <select class="rte-select" @change="setHeading" :value="activeHeading" aria-label="Style de paragraphe">
          <option value="">Paragraphe</option>
          <option value="h1">Titre 1</option>
          <option value="h2">Titre 2</option>
          <option value="h3">Titre 3</option>
          <option value="h4">Titre 4</option>
          <option value="h5">Titre 5</option>
          <option value="h6">Titre 6</option>
          <option value="pre">Code</option>
          <option value="blockquote">Citation</option>
        </select>
      </div>

      <!-- Formatage de base -->
      <div class="rte-group">
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.bold }"
          @click="exec('bold')" title="Gras (Ctrl+B)" aria-label="Gras" aria-pressed="fmt.bold.toString()">
          <svg viewBox="0 0 24 24"><path d="M8 11h4.5a2.5 2.5 0 0 1 0 5H8v-5zm0-5h4a2 2 0 0 1 0 4H8V6zm6.5 4.5A3.5 3.5 0 0 0 12.5 4H6v16h7a3.5 3.5 0 0 0 1.5-6.68V10.5z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.italic }"
          @click="exec('italic')" title="Italique (Ctrl+I)" aria-label="Italique">
          <svg viewBox="0 0 24 24"><path d="M10 5v3h2.2l-3.4 8H6v3h8v-3h-2.2l3.4-8H18V5h-8z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.underline }"
          @click="exec('underline')" title="Souligné (Ctrl+U)" aria-label="Souligné">
          <svg viewBox="0 0 24 24"><path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.strikethrough }"
          @click="exec('strikethrough')" title="Barré" aria-label="Barré">
          <svg viewBox="0 0 24 24"><path d="M10 19h4v-3h-4v3zm-2-8v3h8v-3H8zm12-4H4v3h16V7z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.superscript }"
          @click="exec('superscript')" title="Exposant" aria-label="Exposant">
          <svg viewBox="0 0 24 24"><path d="M16 7.41L11.41 12 16 16.59 14.59 18 10 13.41 5.41 18 4 16.59 8.59 12 4 7.41 5.41 6 10 10.59 14.59 6 16 7.41zM21.85 9h-3.02l1.84-1.97c.38-.4.61-.94.61-1.53C21.28 4.12 20.16 3 18.78 3c-1.38 0-2.5 1.12-2.5 2.5h1.5c0-.55.45-1 1-1s1 .45 1 1c0 .28-.11.53-.29.71L16.5 9.5V11h5v-2h.35z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.subscript }"
          @click="exec('subscript')" title="Indice" aria-label="Indice">
          <svg viewBox="0 0 24 24"><path d="M16 7.41L11.41 12 16 16.59 14.59 18 10 13.41 5.41 18 4 16.59 8.59 12 4 7.41 5.41 6 10 10.59 14.59 6 16 7.41zM21.85 20h-3.02l1.84-1.97c.38-.4.61-.94.61-1.53C21.28 15.12 20.16 14 18.78 14c-1.38 0-2.5 1.12-2.5 2.5h1.5c0-.55.45-1 1-1s1 .45 1 1c0 .28-.11.53-.29.71L16.5 20.5V22h5v-2h.35z"/></svg>
        </button>
      </div>

      <!-- Alignement -->
      <div class="rte-group">
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.alignLeft }"
          @click="exec('justifyLeft')" title="Aligner à gauche" aria-label="Aligner à gauche">
          <svg viewBox="0 0 24 24"><path d="M3 7h18v2H3V7zm0 4h14v2H3v-2zm0 4h18v2H3v-2zm0 4h14v2H3v-2z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.alignCenter }"
          @click="exec('justifyCenter')" title="Centrer" aria-label="Centrer">
          <svg viewBox="0 0 24 24"><path d="M3 7h18v2H3V7zm2 4h14v2H5v-2zm-2 4h18v2H3v-2zm2 4h14v2H5v-2z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.alignRight }"
          @click="exec('justifyRight')" title="Aligner à droite" aria-label="Aligner à droite">
          <svg viewBox="0 0 24 24"><path d="M3 7h18v2H3V7zm4 4h14v2H7v-2zm-4 4h18v2H3v-2zm4 4h14v2H7v-2z"/></svg>
        </button>
        <button type="button" class="rte-btn" :class="{ 'is-active': fmt.alignJustify }"
          @click="exec('justifyFull')" title="Justifier" aria-label="Justifier">
          <svg viewBox="0 0 24 24"><path d="M3 7h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/></svg>
        </button>
      </div>

      <!-- Listes + indentation -->
      <div class="rte-group">
        <button type="button" class="rte-btn" @click="exec('insertUnorderedList')" title="Liste à puces" aria-label="Liste à puces">
          <svg viewBox="0 0 24 24"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="exec('insertOrderedList')" title="Liste numérotée" aria-label="Liste numérotée">
          <svg viewBox="0 0 24 24"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="exec('outdent')" title="Diminuer le retrait" aria-label="Diminuer le retrait">
          <svg viewBox="0 0 24 24"><path d="M11 17h10v-2H11v2zm-8-5l4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="exec('indent')" title="Augmenter le retrait" aria-label="Augmenter le retrait">
          <svg viewBox="0 0 24 24"><path d="M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
        </button>
      </div>

      <!-- Couleur texte -->
      <div class="rte-group rte-color-group" ref="colorGroupRef">
        <button type="button" class="rte-btn rte-color-btn"
          @click.stop="toggleColorPicker" title="Couleur du texte" aria-label="Couleur du texte"
          :aria-expanded="showColorPicker">
          <svg viewBox="0 0 24 24"><path d="M11.308 4L5.393 18h2.237l1.166-3h6.408l1.166 3h2.237L12.692 4h-1.384zM9.61 13l2.39-6.159L14.39 13H9.61z"/></svg>
          <span class="rte-color-indicator" :style="{ background: currentTextColor }"></span>
        </button>
        <div v-show="showColorPicker" class="rte-color-picker" @click.stop role="dialog" aria-label="Palette de couleurs">
          <div class="rte-color-section">
            <span class="rte-color-label">Couleurs de texte</span>
            <div class="rte-palette">
              <button
                v-for="color in COLORS"
                :key="'tc-'+color"
                type="button"
                class="rte-swatch"
                :style="{ background: color }"
                :title="color"
                @click="applyTextColor(color)"
              ></button>
            </div>
          </div>
          <div class="rte-color-section">
            <span class="rte-color-label">Surlignage</span>
            <div class="rte-palette">
              <button
                v-for="color in HIGHLIGHT_COLORS"
                :key="'hl-'+color"
                type="button"
                class="rte-swatch"
                :style="{ background: color }"
                :title="color"
                @click="applyHighlight(color)"
              ></button>
            </div>
          </div>
          <button type="button" class="rte-color-reset" @click="resetColor">
            ✕ Réinitialiser la couleur
          </button>
        </div>
      </div>

      <!-- Transformation de texte -->
      <div class="rte-group">
        <button type="button" class="rte-btn" @click="transformText('uppercase')" title="MAJUSCULES" aria-label="Mettre en majuscules">Aa↑</button>
        <button type="button" class="rte-btn" @click="transformText('lowercase')" title="minuscules" aria-label="Mettre en minuscules">aa↓</button>
        <button type="button" class="rte-btn" @click="transformText('capitalize')" title="Première Lettre Majuscule" aria-label="Capitaliser">Aa</button>
      </div>

      <!-- Tableaux -->
      <div class="rte-group">
        <button type="button" class="rte-btn" @click="openTableModal" title="Insérer un tableau" aria-label="Insérer un tableau">
          <svg viewBox="0 0 24 24"><path d="M5 3h14c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zm0 2v4h14V5H5zm14 6H5v4h14v-4zm0 6H5v2h14v-2z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="tableAction('insertRow')" title="Ajouter une ligne" aria-label="Ajouter une ligne">
          <svg viewBox="0 0 24 24"><path d="M20 13H4v-2h16v2zM3 17h18v-2H3v2zM3 9h18V7H3v2zm15 6h2v2h2v-2h2v-2h-2v-2h-2v2h-2v2z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="tableAction('insertCol')" title="Ajouter une colonne" aria-label="Ajouter une colonne">
          <svg viewBox="0 0 24 24"><path d="M11 4v16h2V4h-2zM4 18h16v-2H4v2zm0-4h16v-2H4v2zm0-4h16V8H4v2zm0-4h16V4H4v2zM19 10v2h2v2h2v-2h2v-2h-2V8h-2v2h-2z"/></svg>
        </button>
        <button type="button" class="rte-btn rte-btn--danger" @click="tableAction('deleteRow')" title="Supprimer la ligne" aria-label="Supprimer la ligne">
          <svg viewBox="0 0 24 24"><path d="M19 13H5v-2h14v2zm0-4H5V7h14v2zM5 17h14v-2H5v2z"/></svg>
        </button>
        <button type="button" class="rte-btn rte-btn--danger" @click="tableAction('deleteCol')" title="Supprimer la colonne" aria-label="Supprimer la colonne">
          <svg viewBox="0 0 24 24"><path d="M11 4v16h2V4h-2zM4 18h16v-2H4v2zm0-4h16v-2H4v2zm0-4h16V8H4v2zm0-4h16V4H4v2z"/></svg>
        </button>
      </div>

      <!-- Liens & médias -->
      <div class="rte-group">
        <button type="button" class="rte-btn" @click="openLinkModal" title="Insérer un lien (Ctrl+K)" aria-label="Insérer un lien">
          <svg viewBox="0 0 24 24"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="openImageModal" title="Image" aria-label="Insérer une image">
          <svg viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="openVideoModal" title="Vidéo YouTube/Vimeo" aria-label="Insérer une vidéo">
          <svg viewBox="0 0 24 24"><path d="M10 8v8l5-4-5-4zm9-5H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>
        </button>
        <button type="button" class="rte-btn" @click="exec('insertHorizontalRule')" title="Ligne de séparation" aria-label="Ligne de séparation">
          <svg viewBox="0 0 24 24"><path d="M19 13H5v-2h14v2z"/></svg>
        </button>
      </div>

      <!-- Compteur de mots -->
      <div class="rte-wordcount" aria-live="polite">
        <span>{{ wordCount }} mot{{ wordCount !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- ───── Zone d'édition ───── -->
    <div
      ref="editorRef"
      class="rte-content rte-editable"
      contenteditable="true"
      :id="contentId"
      role="textbox"
      aria-multiline="true"
      :aria-label="label || 'Zone de rédaction'"
      @input="handleInput"
      @keydown="handleKeyDown"
      @paste="handlePaste"
      @mouseup="updateFormats"
      @keyup="updateFormats"
      @focus="isFocused = true"
      @blur="isFocused = false"
    ></div>

    <!-- ───── Modal Tableau ───── -->
    <Teleport to="body">
      <div v-if="showTableModal" class="rte-overlay" @click.self="showTableModal = false" role="dialog" aria-modal="true" aria-label="Insérer un tableau">
        <div class="rte-modal">
          <div class="rte-modal__header">
            <h3>Insérer un tableau</h3>
            <button type="button" class="rte-modal__close" @click="showTableModal = false" aria-label="Fermer">✕</button>
          </div>
          <div class="rte-modal__body">
            <p class="rte-modal__hint">Survolez la grille pour définir la taille</p>
            <div class="rte-table-grid">
              <div v-for="r in 8" :key="'r'+r" class="rte-table-row">
                <button
                  v-for="c in 8" :key="'c'+c"
                  type="button"
                  class="rte-table-cell"
                  :class="{ 'is-selected': r <= tableRows && c <= tableCols }"
                  @mouseenter="tableRows = r; tableCols = c"
                  @click="insertTable"
                  :aria-label="`Tableau ${r}×${c}`"
                ></button>
              </div>
            </div>
            <p class="rte-table-size">{{ tableRows }} × {{ tableCols }}</p>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ───── Modal Lien ───── -->
    <Teleport to="body">
      <div v-if="showLinkModal" class="rte-overlay" @click.self="showLinkModal = false" role="dialog" aria-modal="true" aria-label="Insérer un lien">
        <div class="rte-modal">
          <div class="rte-modal__header">
            <h3>Insérer un lien</h3>
            <button type="button" class="rte-modal__close" @click="showLinkModal = false" aria-label="Fermer">✕</button>
          </div>
          <div class="rte-modal__body">
            <label class="rte-modal__label">URL</label>
            <input ref="linkInputRef" type="url" v-model="linkUrl" placeholder="https://exemple.com"
              class="rte-modal__input" @keyup.enter="applyLink" />
            <label class="rte-modal__label">Texte affiché (optionnel)</label>
            <input type="text" v-model="linkText" placeholder="Mon lien"
              class="rte-modal__input" @keyup.enter="applyLink" />
            <label class="rte-modal__check">
              <input type="checkbox" v-model="linkNewTab" />
              Ouvrir dans un nouvel onglet
            </label>
          </div>
          <div class="rte-modal__footer">
            <button type="button" class="rte-modal__btn rte-modal__btn--ghost" @click="showLinkModal = false">Annuler</button>
            <button type="button" class="rte-modal__btn rte-modal__btn--primary" @click="applyLink">Insérer</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ───── Modal Image ───── -->
    <Teleport to="body">
      <div v-if="showImageModal" class="rte-overlay" @click.self="showImageModal = false" role="dialog" aria-modal="true" aria-label="Insérer une image">
        <div class="rte-modal">
          <div class="rte-modal__header">
            <h3>Insérer une image</h3>
            <button type="button" class="rte-modal__close" @click="showImageModal = false" aria-label="Fermer">✕</button>
          </div>
          <div class="rte-modal__body">
            <div class="rte-modal__tabs">
              <button type="button" :class="['rte-modal__tab', imageTab === 'url' && 'is-active']" @click="imageTab = 'url'">URL</button>
              <button type="button" :class="['rte-modal__tab', imageTab === 'upload' && 'is-active']" @click="imageTab = 'upload'">Téléverser</button>
            </div>
            <template v-if="imageTab === 'url'">
              <label class="rte-modal__label">URL de l'image</label>
              <input ref="imageInputRef" type="url" v-model="imageUrl" placeholder="https://exemple.com/image.jpg"
                class="rte-modal__input" @keyup.enter="applyImage" />
            </template>
            <template v-else>
              <label class="rte-modal__label">Choisir un fichier</label>
              <div class="rte-upload-zone" @click="$refs.fileInput.click()" @dragover.prevent @drop.prevent="handleDrop">
                <svg viewBox="0 0 24 24" width="32" height="32"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/></svg>
                <p>Glissez une image ou cliquez pour parcourir</p>
                <span v-if="imageUploadName">{{ imageUploadName }}</span>
              </div>
              <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleImageUpload" />
            </template>
            <div v-if="imageUrl" class="rte-image-preview">
              <img :src="imageUrl" alt="Aperçu" />
            </div>
            <label class="rte-modal__label" style="margin-top:12px">Texte alternatif (accessibilité)</label>
            <input type="text" v-model="imageAlt" placeholder="Description de l'image"
              class="rte-modal__input" />
          </div>
          <div class="rte-modal__footer">
            <button type="button" class="rte-modal__btn rte-modal__btn--ghost" @click="showImageModal = false">Annuler</button>
            <button type="button" class="rte-modal__btn rte-modal__btn--primary" @click="applyImage" :disabled="!imageUrl">Insérer</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ───── Modal Vidéo ───── -->
    <Teleport to="body">
      <div v-if="showVideoModal" class="rte-overlay" @click.self="showVideoModal = false" role="dialog" aria-modal="true" aria-label="Insérer une vidéo">
        <div class="rte-modal">
          <div class="rte-modal__header">
            <h3>Insérer une vidéo</h3>
            <button type="button" class="rte-modal__close" @click="showVideoModal = false" aria-label="Fermer">✕</button>
          </div>
          <div class="rte-modal__body">
            <label class="rte-modal__label">URL YouTube ou Vimeo</label>
            <input ref="videoInputRef" type="url" v-model="videoUrl"
              placeholder="https://youtube.com/watch?v=… ou https://vimeo.com/…"
              class="rte-modal__input" @keyup.enter="applyVideo" />
            <p class="rte-modal__hint">Formats supportés : youtube.com, youtu.be, vimeo.com</p>
          </div>
          <div class="rte-modal__footer">
            <button type="button" class="rte-modal__btn rte-modal__btn--ghost" @click="showVideoModal = false">Annuler</button>
            <button type="button" class="rte-modal__btn rte-modal__btn--primary" @click="applyVideo" :disabled="!videoUrl">Insérer</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
let _rteCounter = 0

const COLORS = [
  '#000000','#1a1a2e','#16213e','#0f3460','#533483','#e94560',
  '#ffffff','#f8f9fa','#e9ecef','#dee2e6','#adb5bd','#6c757d',
  '#d62828','#f77f00','#fcbf49','#eae2b7','#003049','#588157',
  '#c77dff','#7b2d8b','#023e8a','#0077b6','#00b4d8','#90e0ef',
  '#2dc653','#52b788','#b7e4c7','#d8f3dc','#ffd166','#ef476f',
]

const HIGHLIGHT_COLORS = [
  '#ffff00','#ffd700','#ff9500','#ff3b30',
  '#34c759','#5ac8fa','#007aff','#af52de',
  '#ffffff','#f2f2f7',
]

export default {
  name: 'RichTextEditor',

  props: {
    modelValue: { type: String, default: '' },
    label:      { type: String, default: '' },
    minHeight:  { type: Number, default: 300 },
    placeholder:{ type: String, default: 'Rédigez votre contenu ici…' },
  },

  emits: ['update:modelValue'],

  data() {
    _rteCounter++
    return {
      uid:        `rte-${_rteCounter}-${Math.random().toString(36).slice(2, 7)}`,
      contentId:  `rte-content-${_rteCounter}`,

      // State
      content:        this.modelValue,
      history:        [this.modelValue],
      historyIndex:   0,
      isFocused:      false,
      savedSelection: null,
      wordCount:      0,

      // Active format state
      fmt: {
        bold: false, italic: false, underline: false,
        strikethrough: false, superscript: false, subscript: false,
        alignLeft: false, alignCenter: false, alignRight: false, alignJustify: false,
      },
      activeHeading:    '',
      currentTextColor: '#000000',

      // Color picker
      showColorPicker: false,
      COLORS,
      HIGHLIGHT_COLORS,

      // Modals
      showTableModal: false, tableRows: 3, tableCols: 3,
      showLinkModal:  false, linkUrl: '', linkText: '', linkNewTab: true,
      showImageModal: false, imageUrl: '', imageAlt: '', imageTab: 'url', imageUploadName: '',
      showVideoModal: false, videoUrl: '',
    }
  },

  watch: {
    modelValue(val) {
      if (val !== this.content) {
        this.content = val
        if (this.$refs.editorRef) {
          this.$refs.editorRef.innerHTML = val
          this.calcWordCount()
        }
      }
    },
  },

  mounted() {
    const el = this.$refs.editorRef
    el.innerHTML = this.content
    // Placeholder via attribut data
    el.dataset.placeholder = this.placeholder
    this.calcWordCount()
    document.addEventListener('selectionchange', this.updateFormats)
    document.addEventListener('click', this.onDocClick)
  },

  beforeUnmount() {
    document.removeEventListener('selectionchange', this.updateFormats)
    document.removeEventListener('click', this.onDocClick)
  },

  methods: {
    // ── Core ──────────────────────────────────────────────
    exec(cmd, value = null) {
      this.$refs.editorRef.focus()
      document.execCommand(cmd, false, value)
      this.handleInput()
      this.updateFormats()
    },

    handleInput() {
      const html = this.$refs.editorRef.innerHTML
      this.content = html
      this.$emit('update:modelValue', html)
      this.addHistory(html)
      this.calcWordCount()
    },

    handleKeyDown(e) {
      if (e.ctrlKey || e.metaKey) {
        const map = { b:'bold', i:'italic', u:'underline' }
        if (map[e.key]) { e.preventDefault(); this.exec(map[e.key]); return }
        if (e.key === 'k') { e.preventDefault(); this.openLinkModal(); return }
        if (e.key === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); return }
        if (e.key === 'y') { e.preventDefault(); this.redo(); return }
      }
      // Tab → indent
      if (e.key === 'Tab') {
        e.preventDefault()
        e.shiftKey ? this.exec('outdent') : this.exec('indent')
      }
    },

    handlePaste(e) {
      e.preventDefault()
      // Essaye de récupérer le HTML propre, sinon texte brut
      const html  = e.clipboardData.getData('text/html')
      const plain = e.clipboardData.getData('text/plain')
      if (html) {
        // Nettoie les styles problématiques (Word, Google Docs)
        const clean = this.sanitizePaste(html)
        document.execCommand('insertHTML', false, clean)
      } else {
        document.execCommand('insertText', false, plain)
      }
      this.handleInput()
    },

    sanitizePaste(html) {
      const div = document.createElement('div')
      div.innerHTML = html
      // Retire les scripts, style, meta
      div.querySelectorAll('script,style,meta,link,head').forEach(el => el.remove())
      // Retire les attributs de style Word/Gdocs sauf color et font-weight
      div.querySelectorAll('[style]').forEach(el => {
        const allowed = []
        const s = el.style
        if (s.color)      allowed.push(`color:${s.color}`)
        if (s.fontWeight) allowed.push(`font-weight:${s.fontWeight}`)
        if (s.fontStyle)  allowed.push(`font-style:${s.fontStyle}`)
        el.setAttribute('style', allowed.join(';'))
      })
      return div.innerHTML
    },

    // ── Formats ───────────────────────────────────────────
    updateFormats() {
      this.$nextTick(() => {
        this.fmt = {
          bold:        document.queryCommandState('bold'),
          italic:      document.queryCommandState('italic'),
          underline:   document.queryCommandState('underline'),
          strikethrough: document.queryCommandState('strikethrough'),
          superscript: document.queryCommandState('superscript'),
          subscript:   document.queryCommandState('subscript'),
          alignLeft:   document.queryCommandState('justifyLeft'),
          alignCenter: document.queryCommandState('justifyCenter'),
          alignRight:  document.queryCommandState('justifyRight'),
          alignJustify:document.queryCommandState('justifyFull'),
        }
        this.activeHeading = (document.queryCommandValue('formatBlock') || '').toLowerCase()
        const color = document.queryCommandValue('foreColor')
        if (color) this.currentTextColor = color
      })
    },

    setHeading(e) {
      const v = e.target.value
      if (v === 'blockquote') {
        document.execCommand('formatBlock', false, 'blockquote')
      } else {
        this.exec('formatBlock', v || 'p')
      }
    },

    // ── Texte ─────────────────────────────────────────────
    transformText(type) {
      const sel = window.getSelection()
      if (!sel.toString()) return
      const range = sel.getRangeAt(0)
      const text  = sel.toString()
      const out   =
        type === 'uppercase' ? text.toUpperCase() :
        type === 'lowercase' ? text.toLowerCase() :
        text.replace(/\b\w/g, l => l.toUpperCase())
      range.deleteContents()
      range.insertNode(document.createTextNode(out))
      this.handleInput()
    },

    calcWordCount() {
      const text = this.$refs.editorRef?.innerText || ''
      this.wordCount = text.trim() ? text.trim().split(/\s+/).length : 0
    },

    // ── Couleurs ──────────────────────────────────────────
    toggleColorPicker() { this.showColorPicker = !this.showColorPicker },

    onDocClick(e) {
      if (this.$refs.colorGroupRef && !this.$refs.colorGroupRef.contains(e.target)) {
        this.showColorPicker = false
      }
    },

    applyTextColor(color) {
      this.currentTextColor = color
      this.exec('foreColor', color)
      this.showColorPicker = false
    },

    applyHighlight(color) {
      this.exec('hiliteColor', color)
      this.showColorPicker = false
    },

    resetColor() {
      this.exec('removeFormat')
      this.currentTextColor = '#000000'
      this.showColorPicker = false
    },

    // ── Sélection ─────────────────────────────────────────
    saveSelection() {
      const sel = window.getSelection()
      if (sel.rangeCount > 0) this.savedSelection = sel.getRangeAt(0).cloneRange()
    },

    restoreSelection() {
      if (!this.savedSelection) return
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(this.savedSelection)
    },

    // ── Tableau ───────────────────────────────────────────
    openTableModal() {
      this.saveSelection()
      this.tableRows = 3; this.tableCols = 3
      this.showTableModal = true
    },

    insertTable() {
      let html = '<table><thead><tr>'
      for (let j = 0; j < this.tableCols; j++) html += '<th></th>'
      html += '</tr></thead><tbody>'
      for (let i = 1; i < this.tableRows; i++) {
        html += '<tr>'
        for (let j = 0; j < this.tableCols; j++) html += '<td></td>'
        html += '</tr>'
      }
      html += '</tbody></table><p></p>'
      this.restoreSelection()
      document.execCommand('insertHTML', false, html)
      this.showTableModal = false
      this.$refs.editorRef.focus()
      this.handleInput()
    },

    tableAction(action) {
      const sel  = window.getSelection()
      const node = sel.focusNode
      if (!node) return
      const cell  = node.nodeType === 3 ? node.parentNode : node
      const td    = cell?.closest('td,th')
      const row   = td?.closest('tr')
      const table = td?.closest('table')
      if (!row || !table) return

      if (action === 'insertRow') {
        const nr = row.cloneNode(true)
        nr.querySelectorAll('td,th').forEach(c => c.innerHTML = '')
        row.parentNode.insertBefore(nr, row.nextSibling)
      } else if (action === 'insertCol') {
        const idx = Array.from(row.cells).indexOf(td)
        Array.from(table.rows).forEach(r => {
          const nc = document.createElement(r.closest('thead') ? 'th' : 'td')
          r.insertBefore(nc, r.cells[idx + 1] || null)
        })
      } else if (action === 'deleteRow') {
        if (table.rows.length > 1) row.remove()
      } else if (action === 'deleteCol') {
        const idx = Array.from(row.cells).indexOf(td)
        Array.from(table.rows).forEach(r => { if (r.cells[idx]) r.cells[idx].remove() })
      }
      this.handleInput()
    },

    // ── Lien ──────────────────────────────────────────────
    openLinkModal() {
      this.saveSelection()
      this.linkUrl = ''; this.linkText = ''
      // Pré-remplit le texte avec la sélection
      const sel = window.getSelection()
      if (sel.toString()) this.linkText = sel.toString()
      this.showLinkModal = true
      this.$nextTick(() => this.$refs.linkInputRef?.focus())
    },

    applyLink() {
      if (!this.linkUrl) return
      // Validation basique
      let url = this.linkUrl.trim()
      if (!/^https?:\/\//i.test(url)) url = 'https://' + url

      this.restoreSelection()
      const target = this.linkNewTab ? ' target="_blank" rel="noopener noreferrer"' : ''
      const sel = window.getSelection()

      if (sel.toString()) {
        document.execCommand('createLink', false, url)
        // Ajoute target si nouvel onglet
        if (this.linkNewTab) {
          const a = sel.anchorNode?.parentElement?.closest('a')
            || sel.focusNode?.parentElement?.closest('a')
          if (a) { a.target = '_blank'; a.rel = 'noopener noreferrer' }
        }
      } else {
        const text = this.linkText || url
        document.execCommand('insertHTML', false, `<a href="${url}"${target}>${text}</a>`)
      }
      this.showLinkModal = false
      this.savedSelection = null
      this.$refs.editorRef.focus()
      this.handleInput()
    },

    // ── Image ─────────────────────────────────────────────
    openImageModal() {
      this.saveSelection()
      this.imageUrl = ''; this.imageAlt = ''; this.imageUploadName = ''; this.imageTab = 'url'
      this.showImageModal = true
      this.$nextTick(() => this.$refs.imageInputRef?.focus())
    },

    handleImageUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      this.imageUploadName = file.name
      const reader = new FileReader()
      reader.onload = ev => { this.imageUrl = ev.target.result }
      reader.readAsDataURL(file)
    },

    handleDrop(e) {
      const file = e.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) return
      this.imageUploadName = file.name
      const reader = new FileReader()
      reader.onload = ev => { this.imageUrl = ev.target.result }
      reader.readAsDataURL(file)
    },

    applyImage() {
      if (!this.imageUrl) return
      this.restoreSelection()
      const alt = this.imageAlt.replace(/"/g, '&quot;')
      const html = `<figure class="rte-figure"><img src="${this.imageUrl}" alt="${alt}" />${this.imageAlt ? `<figcaption>${alt}</figcaption>` : ''}</figure>`
      document.execCommand('insertHTML', false, html)
      this.showImageModal = false
      this.savedSelection = null
      this.$refs.editorRef.focus()
      this.handleInput()
    },

    // ── Vidéo ─────────────────────────────────────────────
    openVideoModal() {
      this.saveSelection()
      this.videoUrl = ''
      this.showVideoModal = true
      this.$nextTick(() => this.$refs.videoInputRef?.focus())
    },

    applyVideo() {
      if (!this.videoUrl) return
      let embed = this.videoUrl.trim()
      const yt1 = embed.match(/[?&]v=([^&]+)/)
      const yt2 = embed.match(/youtu\.be\/([^?]+)/)
      const vim = embed.match(/vimeo\.com\/(\d+)/)
      if (yt1)      embed = `https://www.youtube.com/embed/${yt1[1]}`
      else if (yt2) embed = `https://www.youtube.com/embed/${yt2[1]}`
      else if (vim) embed = `https://player.vimeo.com/video/${vim[1]}`
      const html = `<div class="rte-video-wrapper"><iframe src="${embed}" frameborder="0" allowfullscreen loading="lazy"></iframe></div>`
      this.restoreSelection()
      document.execCommand('insertHTML', false, html)
      this.showVideoModal = false
      this.savedSelection = null
      this.$refs.editorRef.focus()
      this.handleInput()
    },

    // ── Historique ────────────────────────────────────────
    addHistory(content) {
      if (this.history[this.historyIndex] === content) return
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(content)
      if (this.history.length > 100) this.history.shift()
      else this.historyIndex++
    },

    undo() {
      if (this.historyIndex <= 0) return
      this.historyIndex--
      this.applyHistory()
    },

    redo() {
      if (this.historyIndex >= this.history.length - 1) return
      this.historyIndex++
      this.applyHistory()
    },

    applyHistory() {
      this.content = this.history[this.historyIndex]
      this.$refs.editorRef.innerHTML = this.content
      this.$emit('update:modelValue', this.content)
      this.calcWordCount()
    },
  },
}
</script>

<style>
/*
 * ═══════════════════════════════════════════════════════════════════════
 *  RTE — STYLES GLOBAUX DE CONTENU
 *  Importez cette section dans votre CSS global (ou un fichier séparé
 *  rte-content.css) afin que le rendu soit IDENTIQUE partout :
 *    - dans l'éditeur          → .rte-content.rte-editable
 *    - dans les pages de lecture → .rte-content (sans .rte-editable)
 *    - dans les emails ou PDF  → ajoutez .rte-content à votre wrapper
 * ═══════════════════════════════════════════════════════════════════════
 */

/* ── Variables ────────────────────────────────────────────────────────── */
:root {
  --rte-font-body:    'Georgia', 'Times New Roman', serif;
  --rte-font-ui:      system-ui, -apple-system, 'Segoe UI', sans-serif;
  --rte-font-mono:    'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;

  --rte-color-text:       #1a1a2e;
  --rte-color-muted:      #4a5568;
  --rte-color-border:     #e2e8f0;
  --rte-color-bg:         #ffffff;
  --rte-color-surface:    #f8fafc;
  --rte-color-accent:     #0f3460;
  --rte-color-accent-alt: #e94560;
  --rte-color-link:       #0077b6;
  --rte-color-code-bg:    #1a1a2e;
  --rte-color-code-text:  #cdd9e5;

  --rte-radius:     6px;
  --rte-shadow-sm:  0 1px 3px rgba(0,0,0,.07), 0 1px 2px rgba(0,0,0,.05);
  --rte-shadow-md:  0 4px 12px rgba(0,0,0,.1), 0 2px 4px rgba(0,0,0,.06);
  --rte-shadow-lg:  0 20px 40px rgba(0,0,0,.15);
}

/* ── Contenu formaté (partagé éditeur + lecture) ─────────────────────── */
.rte-content {
  font-family: var(--rte-font-body);
  font-size: 1.0625rem;
  line-height: 1.75;
  color: var(--rte-color-text);
}

.rte-content h1,.rte-content h2,.rte-content h3,
.rte-content h4,.rte-content h5,.rte-content h6 {
  font-family: var(--rte-font-ui);
  font-weight: 700;
  line-height: 1.25;
  margin: 1.75em 0 .5em;
  color: var(--rte-color-accent);
}

.rte-content h1 { font-size: 2.25rem; letter-spacing: -.025em; }
.rte-content h2 { font-size: 1.75rem; letter-spacing: -.015em; }
.rte-content h3 { font-size: 1.375rem; }
.rte-content h4 { font-size: 1.125rem; }
.rte-content h5 { font-size: 1rem; text-transform: uppercase; letter-spacing: .05em; }
.rte-content h6 { font-size: .875rem; text-transform: uppercase; letter-spacing: .075em; color: var(--rte-color-muted); }

.rte-content p  { margin: 0 0 1.125em; }
.rte-content strong { font-weight: 700; }
.rte-content em     { font-style: italic; }
.rte-content u      { text-decoration: underline; text-underline-offset: 3px; }
.rte-content del,
.rte-content s      { text-decoration: line-through; color: var(--rte-color-muted); }
.rte-content sup { vertical-align: super; font-size: .75em; }
.rte-content sub { vertical-align: sub;   font-size: .75em; }

.rte-content a {
  color: var(--rte-color-link);
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color .15s;
}
.rte-content a:hover { color: var(--rte-color-accent-alt); }

.rte-content ul,
.rte-content ol  { margin: .75em 0 .75em 1.5em; padding: 0; }
.rte-content li  { margin: .375em 0; }
.rte-content ul  { list-style: disc; }
.rte-content ol  { list-style: decimal; }

.rte-content blockquote {
  margin: 1.5em 0;
  padding: .75em 1.25em;
  border-left: 4px solid var(--rte-color-accent-alt);
  background: #fff5f7;
  border-radius: 0 var(--rte-radius) var(--rte-radius) 0;
  font-style: italic;
  color: var(--rte-color-muted);
}

.rte-content pre {
  margin: 1.25em 0;
  padding: 1.25em 1.5em;
  background: var(--rte-color-code-bg);
  color: var(--rte-color-code-text);
  border-radius: var(--rte-radius);
  font-family: var(--rte-font-mono);
  font-size: .875rem;
  line-height: 1.6;
  overflow-x: auto;
}

.rte-content code {
  font-family: var(--rte-font-mono);
  background: #f1f5f9;
  color: var(--rte-color-accent-alt);
  padding: .15em .4em;
  border-radius: 4px;
  font-size: .875em;
}

.rte-content pre code {
  background: none;
  color: inherit;
  padding: 0;
  font-size: inherit;
}

.rte-content hr {
  border: none;
  height: 2px;
  background: linear-gradient(to right, var(--rte-color-accent), var(--rte-color-accent-alt));
  border-radius: 1px;
  margin: 2em 0;
  opacity: .35;
}

/* Tables */
.rte-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5em 0;
  font-size: .9375rem;
  border-radius: var(--rte-radius);
  overflow: hidden;
  box-shadow: var(--rte-shadow-sm);
}
.rte-content th {
  background: var(--rte-color-accent);
  color: #ffffff;
  font-family: var(--rte-font-ui);
  font-weight: 600;
  font-size: .8125rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  padding: 10px 14px;
  text-align: left;
}
.rte-content td {
  padding: 9px 14px;
  border-bottom: 1px solid var(--rte-color-border);
  vertical-align: top;
}
.rte-content tr:last-child td { border-bottom: none; }
.rte-content tbody tr:nth-child(even) { background: var(--rte-color-surface); }
.rte-content tbody tr:hover { background: #edf2f7; }

/* Images & médias */
.rte-content figure.rte-figure {
  margin: 1.5em 0;
  text-align: center;
}
.rte-content figure.rte-figure img {
  max-width: 100%;
  height: auto;
  border-radius: var(--rte-radius);
  box-shadow: var(--rte-shadow-md);
}
.rte-content figcaption {
  margin-top: .5em;
  font-size: .875rem;
  color: var(--rte-color-muted);
  font-style: italic;
}
.rte-content img {
  max-width: 100%;
  height: auto;
  border-radius: var(--rte-radius);
}
.rte-content .rte-video-wrapper {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  margin: 1.5em 0;
  border-radius: var(--rte-radius);
  overflow: hidden;
  box-shadow: var(--rte-shadow-md);
}
.rte-content .rte-video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* ── ÉDITEUR UNIQUEMENT ────────────────────────────────────────────────── */
.rte-wrapper {
  display: flex;
  flex-direction: column;
  border: 1.5px solid var(--rte-color-border);
  border-radius: 10px;
  overflow: visible;
  background: var(--rte-color-bg);
  font-family: var(--rte-font-ui);
  box-shadow: var(--rte-shadow-sm);
  transition: border-color .2s, box-shadow .2s;
}
.rte-wrapper.rte-focused {
  border-color: var(--rte-color-accent);
  box-shadow: 0 0 0 3px rgba(15,52,96,.1), var(--rte-shadow-sm);
}

.rte-label {
  font-family: var(--rte-font-ui);
  font-size: .8125rem;
  font-weight: 600;
  color: var(--rte-color-muted);
  text-transform: uppercase;
  letter-spacing: .06em;
  padding: 10px 14px 0;
}

/* Toolbar */
.rte-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: var(--rte-color-surface);
  border-bottom: 1px solid var(--rte-color-border);
  position: sticky;
  top: 0;
  z-index: 20;
  border-radius: 8px 8px 0 0;
}

.rte-group {
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 0 4px;
  border-right: 1px solid var(--rte-color-border);
}
.rte-group:last-of-type { border-right: none; }

.rte-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 5px;
  border: none;
  background: transparent;
  border-radius: 5px;
  color: var(--rte-color-muted);
  font-family: var(--rte-font-ui);
  font-size: .75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, color .15s, opacity .15s;
  white-space: nowrap;
}
.rte-btn svg { fill: currentColor; flex-shrink: 0; width: 16px; height: 16px; }
.rte-btn:hover { background: var(--rte-color-border); color: var(--rte-color-text); }
.rte-btn.is-active { background: var(--rte-color-accent); color: #fff; }
.rte-btn--danger:hover { background: #fee2e2; color: #b91c1c; }
.rte-btn:disabled { opacity: .35; cursor: not-allowed; }

.rte-select {
  height: 30px;
  padding: 0 6px;
  border: 1px solid var(--rte-color-border);
  border-radius: 5px;
  background: white;
  color: var(--rte-color-text);
  font-family: var(--rte-font-ui);
  font-size: .8125rem;
  cursor: pointer;
  outline: none;
  transition: border-color .15s;
}
.rte-select:hover  { border-color: var(--rte-color-accent); }
.rte-select:focus  { border-color: var(--rte-color-accent); box-shadow: 0 0 0 2px rgba(15,52,96,.12); }

/* Color picker */
.rte-color-group { position: relative; }

.rte-color-btn { flex-direction: column; gap: 2px; padding-bottom: 4px; }
.rte-color-indicator {
  width: 18px; height: 3px;
  border-radius: 2px;
  margin-top: 1px;
}

.rte-color-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: white;
  border: 1px solid var(--rte-color-border);
  border-radius: 8px;
  padding: 12px;
  box-shadow: var(--rte-shadow-lg);
  z-index: 50;
  min-width: 220px;
}
.rte-color-section { margin-bottom: 10px; }
.rte-color-section:last-of-type { margin-bottom: 0; }
.rte-color-label {
  display: block;
  font-size: .6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: var(--rte-color-muted);
  margin-bottom: 6px;
}
.rte-palette {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 3px;
}
.rte-swatch {
  width: 20px; height: 20px;
  border: 1.5px solid rgba(0,0,0,.1);
  border-radius: 4px;
  cursor: pointer;
  transition: transform .12s, box-shadow .12s;
}
.rte-swatch:hover { transform: scale(1.25); box-shadow: 0 2px 6px rgba(0,0,0,.2); }
.rte-color-reset {
  width: 100%;
  margin-top: 8px;
  padding: 5px;
  border: 1px solid var(--rte-color-border);
  border-radius: 4px;
  background: transparent;
  font-family: var(--rte-font-ui);
  font-size: .75rem;
  color: var(--rte-color-muted);
  cursor: pointer;
  transition: background .15s;
}
.rte-color-reset:hover { background: var(--rte-color-border); }

/* Word count */
.rte-wordcount {
  margin-left: auto;
  font-size: .6875rem;
  color: var(--rte-color-muted);
  padding: 0 6px;
  white-space: nowrap;
  font-family: var(--rte-font-ui);
}

/* Zone éditable */
.rte-editable {
  min-height: var(--rte-min-height, 300px);
  max-height: 700px;
  overflow-y: auto;
  padding: 22px 24px;
  outline: none;
  border-radius: 0 0 8px 8px;
}
.rte-editable:empty::before {
  content: attr(data-placeholder);
  color: var(--rte-color-muted);
  opacity: .55;
  pointer-events: none;
}

/* ── Modals ──────────────────────────────────────────────────────────── */
.rte-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10,10,20,.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  animation: rte-fade-in .15s ease;
}

@keyframes rte-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.rte-modal {
  background: white;
  border-radius: 12px;
  max-width: 440px;
  width: 100%;
  box-shadow: var(--rte-shadow-lg);
  overflow: hidden;
  animation: rte-slide-up .2s ease;
}

@keyframes rte-slide-up {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
}

.rte-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--rte-color-border);
}
.rte-modal__header h3 {
  margin: 0;
  font-family: var(--rte-font-ui);
  font-size: 1rem;
  font-weight: 700;
  color: var(--rte-color-text);
}
.rte-modal__close {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: var(--rte-color-muted);
  transition: color .15s;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
}
.rte-modal__close:hover { color: var(--rte-color-text); background: var(--rte-color-border); }

.rte-modal__body   { padding: 20px; }
.rte-modal__footer {
  padding: 14px 20px;
  border-top: 1px solid var(--rte-color-border);
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.rte-modal__label {
  display: block;
  font-family: var(--rte-font-ui);
  font-size: .75rem;
  font-weight: 600;
  color: var(--rte-color-muted);
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: 5px;
}
.rte-modal__input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid var(--rte-color-border);
  border-radius: 6px;
  font-family: var(--rte-font-ui);
  font-size: .9rem;
  color: var(--rte-color-text);
  margin-bottom: 14px;
  box-sizing: border-box;
  transition: border-color .15s;
}
.rte-modal__input:focus {
  outline: none;
  border-color: var(--rte-color-accent);
  box-shadow: 0 0 0 3px rgba(15,52,96,.1);
}
.rte-modal__check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--rte-font-ui);
  font-size: .875rem;
  color: var(--rte-color-muted);
  cursor: pointer;
}
.rte-modal__hint {
  font-family: var(--rte-font-ui);
  font-size: .8125rem;
  color: var(--rte-color-muted);
  margin: -6px 0 14px;
}

.rte-modal__btn {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  font-family: var(--rte-font-ui);
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, opacity .15s;
}
.rte-modal__btn--ghost {
  background: var(--rte-color-surface);
  color: var(--rte-color-muted);
}
.rte-modal__btn--ghost:hover { background: var(--rte-color-border); }
.rte-modal__btn--primary {
  background: var(--rte-color-accent);
  color: #fff;
}
.rte-modal__btn--primary:hover { background: #0a2847; }
.rte-modal__btn--primary:disabled { opacity: .45; cursor: not-allowed; }

/* Tabs image modal */
.rte-modal__tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  background: var(--rte-color-surface);
  border-radius: 6px;
  padding: 3px;
}
.rte-modal__tab {
  flex: 1;
  padding: 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-family: var(--rte-font-ui);
  font-size: .8125rem;
  font-weight: 600;
  color: var(--rte-color-muted);
  cursor: pointer;
  transition: background .15s, color .15s;
}
.rte-modal__tab.is-active { background: white; color: var(--rte-color-accent); box-shadow: var(--rte-shadow-sm); }

/* Upload zone */
.rte-upload-zone {
  border: 2px dashed var(--rte-color-border);
  border-radius: 8px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  color: var(--rte-color-muted);
  font-family: var(--rte-font-ui);
  font-size: .875rem;
  margin-bottom: 12px;
}
.rte-upload-zone svg { fill: var(--rte-color-muted); margin-bottom: 8px; }
.rte-upload-zone p { margin: 4px 0; }
.rte-upload-zone span { font-size: .75rem; font-weight: 600; color: var(--rte-color-accent); }
.rte-upload-zone:hover { border-color: var(--rte-color-accent); background: var(--rte-color-surface); }

/* Image preview */
.rte-image-preview {
  text-align: center;
  margin-bottom: 12px;
}
.rte-image-preview img {
  max-height: 140px;
  border-radius: 6px;
  box-shadow: var(--rte-shadow-sm);
}

/* Table grid */
.rte-table-grid { display: inline-block; margin-bottom: 8px; }
.rte-table-row  { display: flex; gap: 2px; margin-bottom: 2px; }
.rte-table-cell {
  width: 22px; height: 22px;
  border: 1.5px solid var(--rte-color-border);
  border-radius: 3px;
  background: white;
  cursor: pointer;
  transition: background .1s, border-color .1s;
}
.rte-table-cell.is-selected { background: var(--rte-color-accent); border-color: #0a2847; }
.rte-table-size {
  font-family: var(--rte-font-ui);
  font-size: .8125rem;
  color: var(--rte-color-muted);
  text-align: center;
  margin: 6px 0 0;
}

/* ── Responsive ─────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .rte-toolbar { gap: 1px; padding: 4px; }
  .rte-group { padding: 0 2px; }
  .rte-btn { min-width: 26px; height: 26px; }
  .rte-wordcount { display: none; }
}

/* ── Mode sombre ─────────────────────────────────────────────────────── */
@media (prefers-color-scheme: dark) {
  :root {
    --rte-color-text:      #e2e8f0;
    --rte-color-muted:     #94a3b8;
    --rte-color-border:    #334155;
    --rte-color-bg:        #1e293b;
    --rte-color-surface:   #0f172a;
    --rte-color-accent:    #60a5fa;
    --rte-color-accent-alt:#f472b6;
    --rte-color-link:      #93c5fd;
    --rte-color-code-bg:   #0d1117;
    --rte-color-code-text: #cdd9e5;
  }
  .rte-modal { background: #1e293b; }
  .rte-upload-zone { background: #0f172a; }
  .rte-table-cell { background: #1e293b; }
  .rte-modal__tab.is-active { background: #334155; }
  .rte-color-picker { background: #1e293b; }
  .rte-select { background: #1e293b; }
  .rte-modal__input { background: #0f172a; color: #e2e8f0; }
  .rte-content tbody tr:nth-child(even) { background: #0f172a; }
  .rte-content tbody tr:hover { background: #1a2942; }
  .rte-content blockquote { background: rgba(244,114,182,.07); }
  .rte-content code { background: #334155; color: #f472b6; }
}

/* ── Print ──────────────────────────────────────────────────────────── */
@media print {
  .rte-toolbar, .rte-wordcount { display: none !important; }
  .rte-wrapper { border: none; box-shadow: none; }
  .rte-editable { max-height: none; overflow: visible; }
}
</style>